/**
 * Командная строка (Ctrl+K).
 *
 * Одно окно вместо блуждания по разделам: переход к разделу проекта, поиск
 * элемента по названию, создание элемента нужного типа, смена проекта и темы.
 *
 * Список типов и их подписи берутся из реестра — второго перечня в коде нет.
 */

import {
  ActivityIcon,
  CircleHelpIcon,
  ClipboardListIcon,
  ClockIcon,
  FileIcon,
  GaugeIcon,
  GavelIcon,
  BanknoteIcon,
  FlagIcon,
  FolderIcon,
  LayoutDashboardIcon,
  ListIcon,
  MonitorIcon,
  MoonIcon,
  PackageIcon,
  PlusIcon,
  SettingsIcon,
  SunIcon,
  UsersIcon,
  WrenchIcon,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import type { ProjectDto, SearchResultDto } from '@project-os/contracts';
import { getItemTypesByGroup, ITEM_GROUPS, ITEM_GROUP_LABELS_RU } from '@project-os/contracts';
import { useProjects } from '@/api/projects';
import { MIN_SEARCH_LENGTH, useProjectSearch } from '@/api/search';
import { useStages } from '@/api/stages';
import { readRecentItems } from '@/lib/use-local-setting';
import { useTheme } from '@/app/theme';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { getItemIcon } from '@/features/items/item-icon';
import { useCommandPalette } from './command-palette-context';

const SECTIONS = [
  { to: '', label: 'Обзор', icon: LayoutDashboardIcon },
  { to: 'items', label: 'Элементы', icon: ListIcon },
  { to: 'stages', label: 'Этапы', icon: FlagIcon },
  { to: 'assets', label: 'Активы', icon: PackageIcon },
  { to: 'resources', label: 'Ресурсы', icon: WrenchIcon },
  { to: 'finance', label: 'Финансы', icon: BanknoteIcon },
  { to: 'activity', label: 'История', icon: ActivityIcon },
  { to: 'members', label: 'Участники', icon: UsersIcon },
  { to: 'settings', label: 'Настройки', icon: SettingsIcon },
] as const;

/** Задержка перед поиском: набор текста не должен слать запрос на букву. */
const SEARCH_DEBOUNCE_MS = 200;

export function CommandPalette({
  project,
  onQuickAdd,
}: {
  project?: ProjectDto;
  onQuickAdd: (type?: string) => void;
}) {
  const palette = useCommandPalette();
  const navigate = useNavigate();
  const { preference, setPreference } = useTheme();

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');

  const { data: projects = [] } = useProjects();

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // Глобальное сочетание клавиш. Ctrl+K и ⌘K — привычная пара.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'k' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        palette.setOpen(!palette.isOpen);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [palette]);

  useEffect(() => {
    if (!palette.isOpen) setSearch('');
  }, [palette.isOpen]);

  const search$ = useProjectSearch(project?.id, debounced);
  const results = search$.data;
  const searching = debounced.length >= MIN_SEARCH_LENGTH;

  // Этапы ищутся на клиенте: их единицы, и отдельный запрос за списком из
  // пяти строк был бы лишним обращением к серверу.
  const { data: stages = [] } = useStages(project?.id);
  const foundStages = project
    ? stages.filter((stage) => matches(stage.name, debounced)).slice(0, 4)
    : [];

  /**
   * Недавно открытые элементы.
   *
   * Чаще всего человек возвращается к тому, с чем работал минуту назад, а не
   * ищет новое. Список читается при каждом открытии палитры, а не один раз
   * при монтировании: за время сессии он меняется.
   */
  const recent = useMemo(
    () => (project && palette.isOpen ? readRecentItems(project.id).slice(0, 5) : []),
    [project, palette.isOpen],
  );

  // Списки считаются заранее: иначе нельзя решить, показывать ли заголовок
  // группы, а заголовок без содержимого обещает то, чего нет.
  const visibleSections = SECTIONS.filter((section) => matches(section.label, debounced));
  const creatableTypes = ITEM_GROUPS.flatMap((group) =>
    getItemTypesByGroup(group)
      .filter((definition) => matches(definition.labelRu, debounced))
      .map((definition) => ({ definition, group })),
  );

  /**
   * Быстрые действия — самые частые типы вынесены отдельной группой.
   *
   * Полный список из пятнадцати типов остаётся ниже: здесь только те, что
   * создают каждый день, чтобы до них не приходилось прокручивать.
   */
  const QUICK_ACTIONS = [
    { type: 'TASK', label: 'Создать задачу', icon: ClipboardListIcon },
    { type: 'DECISION', label: 'Записать решение', icon: GavelIcon },
    { type: 'METRIC', label: 'Добавить метрику', icon: GaugeIcon },
    { type: 'QUESTION', label: 'Задать вопрос', icon: CircleHelpIcon },
  ] as const;

  const run = (action: () => void) => {
    palette.close();
    action();
  };

  return (
    <CommandDialog
      open={palette.isOpen}
      onOpenChange={palette.setOpen}
      title="Команды"
      description="Поиск по проекту и быстрые действия"
      shouldFilter={false}
    >
      <CommandInput
        placeholder="Куда перейти, что найти или создать…"
        value={search}
        onValueChange={setSearch}
      />
      <CommandList className="max-h-[26rem]">
        <CommandEmpty>
          {searching && search$.isPending ? 'Ищем…' : 'Ничего не найдено'}
        </CommandEmpty>

        {/* Находки идут первыми: за поиском в палитру и приходят */}
        {searching && project ? (
          <>
            <ResultGroup
              heading="Элементы"
              results={results?.items ?? []}
              projectId={project.id}
              onNavigate={(to) => run(() => navigate(to))}
            />
            <ResultGroup
              heading="Обсуждение"
              results={results?.comments ?? []}
              projectId={project.id}
              onNavigate={(to) => run(() => navigate(to))}
            />
            <ResultGroup
              heading="Файлы"
              results={results?.attachments ?? []}
              projectId={project.id}
              onNavigate={(to) => run(() => navigate(to))}
            />
            {(results?.totalCount ?? 0) > 0 ? <CommandSeparator /> : null}
          </>
        ) : null}

        {/* Недавнее показывается, пока не начали искать: во время поиска
            человек ищет конкретное, а не возвращается к прежнему. */}
        {!searching && project && recent.length > 0 ? (
          <>
            <CommandGroup heading="Недавнее">
              {recent.map((entry) => {
                const Icon = getItemIcon(entry.type as Parameters<typeof getItemIcon>[0]);
                return (
                  <CommandItem
                    key={entry.id}
                    value={`recent-${entry.id}`}
                    onSelect={() =>
                      run(() => navigate(`/projects/${project.id}/items?item=${entry.id}`))
                    }
                  >
                    <Icon className="size-4 shrink-0 opacity-70" />
                    <span className="min-w-0 flex-1 truncate">{entry.title}</span>
                    <ClockIcon className="size-3 shrink-0 opacity-40" />
                  </CommandItem>
                );
              })}
            </CommandGroup>
            <CommandSeparator />
          </>
        ) : null}

        {/* Этапы: их немного, поэтому отдельной группой рядом с элементами. */}
        {searching && foundStages.length > 0 && project ? (
          <CommandGroup heading="Этапы">
            {foundStages.map((stage) => (
              <CommandItem
                key={stage.id}
                value={`stage-${stage.id}`}
                onSelect={() => run(() => navigate(`/projects/${project.id}/stages`))}
              >
                <FlagIcon className="size-4 shrink-0 opacity-70" />
                <span className="min-w-0 flex-1 truncate">{stage.name}</span>
                <span className="text-2xs text-muted-foreground">
                  {stage.readiness.done} из {stage.readiness.total}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {project ? (
          <CommandGroup heading="Быстрые действия">
            {QUICK_ACTIONS.filter((action) => matches(action.label, debounced)).map((action) => (
              <CommandItem
                key={action.type}
                value={`quick-${action.type}`}
                onSelect={() => run(() => onQuickAdd(action.type))}
              >
                <action.icon className="size-4 shrink-0 opacity-70" />
                {action.label}
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {project && visibleSections.length > 0 ? (
          <CommandGroup heading="Разделы проекта">
            {visibleSections.map((section) => (
              <CommandItem
                key={section.label}
                value={`section-${section.label}`}
                onSelect={() =>
                  run(() =>
                    navigate(
                      section.to
                        ? `/projects/${project.id}/${section.to}`
                        : `/projects/${project.id}`,
                    ),
                  )
                }
              >
                <section.icon className="size-4 shrink-0 opacity-70" />
                {section.label}
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {project ? (
          <CommandGroup heading="Создать">
            {creatableTypes.map(({ definition, group }) => {
              const Icon = getItemIcon(definition.type);
              return (
                <CommandItem
                  key={definition.type}
                  value={`create-${definition.type}`}
                  onSelect={() => run(() => onQuickAdd(definition.type))}
                >
                  <Icon className="size-4 shrink-0 opacity-70" />
                  <span className="flex-1">{definition.labelRu}</span>
                  <span className="text-2xs text-muted-foreground">
                    {ITEM_GROUP_LABELS_RU[group]}
                  </span>
                </CommandItem>
              );
            })}
            <CommandItem value="create-any" onSelect={() => run(() => onQuickAdd())}>
              <PlusIcon className="size-4 shrink-0 opacity-70" />
              Выбрать тип вручную
            </CommandItem>
          </CommandGroup>
        ) : null}

        {projects.length > 0 ? (
          <CommandGroup heading="Проекты">
            {projects
              .filter((candidate) => matches(candidate.name, debounced))
              .slice(0, 6)
              .map((candidate) => (
                <CommandItem
                  key={candidate.id}
                  value={`project-${candidate.id}`}
                  onSelect={() => run(() => navigate(`/projects/${candidate.id}`))}
                >
                  <FolderIcon className="size-4 shrink-0 opacity-70" />
                  <span className="flex-1 truncate">{candidate.name}</span>
                  {candidate.id === project?.id ? (
                    <span className="text-2xs text-muted-foreground">текущий</span>
                  ) : null}
                </CommandItem>
              ))}
          </CommandGroup>
        ) : null}

        <CommandGroup heading="Оформление">
          {(
            [
              { value: 'dark', label: 'Тёмная тема', icon: MoonIcon },
              { value: 'light', label: 'Светлая тема', icon: SunIcon },
              { value: 'system', label: 'Как в системе', icon: MonitorIcon },
            ] as const
          )
            .filter((option) => matches(option.label, debounced))
            .map((option) => (
              <CommandItem
                key={option.value}
                value={`theme-${option.value}`}
                onSelect={() => run(() => setPreference(option.value))}
              >
                <option.icon className="size-4 shrink-0 opacity-70" />
                <span className="flex-1">{option.label}</span>
                {preference === option.value ? (
                  <span className="text-2xs text-muted-foreground">выбрано</span>
                ) : null}
              </CommandItem>
            ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}

/**
 * Совпадение по подстроке без учёта регистра.
 *
 * Встроенная фильтрация cmdk отключена: она не понимает кириллицу так, как
 * ожидает русскоязычный пользователь, и перемешивает найденные на сервере
 * элементы с командами.
 */
function matches(label: string, query: string): boolean {
  if (!query) return true;
  return label.toLocaleLowerCase('ru').includes(query.toLocaleLowerCase('ru'));
}

/**
 * Группа результатов поиска.
 *
 * Все три вида находок ведут к элементу: комментарий и файл сами по себе
 * никуда не открываются, их смысл — в элементе, к которому они относятся.
 */
function ResultGroup({
  heading,
  results,
  projectId,
  onNavigate,
}: {
  heading: string;
  results: SearchResultDto[];
  projectId: string;
  onNavigate: (to: string) => void;
}) {
  if (results.length === 0) return null;

  return (
    <CommandGroup heading={heading}>
      {results.map((result) => {
        const Icon = result.itemType ? getItemIcon(result.itemType) : FileIcon;
        return (
          <CommandItem
            key={`${result.kind}-${result.id}`}
            value={`${result.kind}-${result.id}`}
            onSelect={() =>
              onNavigate(
                result.itemId
                  ? `/projects/${projectId}/items?item=${result.itemId}`
                  : `/projects/${projectId}/items`,
              )
            }
          >
            <Icon className="size-4 shrink-0 opacity-70" />
            <span className="min-w-0 flex-1">
              <span className="block truncate">{result.title}</span>
              {result.snippet ? (
                <span className="block truncate text-2xs text-muted-foreground">
                  {result.author ? `${result.author}: ` : ''}
                  {result.snippet}
                </span>
              ) : null}
            </span>
            {result.workstreamName ? (
              <span className="shrink-0 text-2xs text-muted-foreground">
                {result.workstreamName}
              </span>
            ) : null}
          </CommandItem>
        );
      })}
    </CommandGroup>
  );
}
